import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Form, Button, Badge, Spinner, Alert, Modal } from 'react-bootstrap';

import coldemailer_api from '../coldemailer_api';
import coldemailer_config from '../coldemailer_config';
import '../coldemailer.css';
import '../coldemailer-styles.css';

const c_RowsPerPageOptions = [10, 25, 50, 100];
const c_DefaultRowsPerPage = 25;

// Kept in step with the status enum on the OutreachRecipient model
const c_StatusFilterOptions = [
	{ value: '', label: 'All statuses' },
	{ value: 'pending', label: 'Pending' },
	{ value: 'queued', label: 'Queued' },
	{ value: 'sent', label: 'Sent' },
	{ value: 'replied', label: 'Replied' },
	{ value: 'bounced', label: 'Bounced' },
	{ value: 'complained', label: 'Complained' },
	{ value: 'recycled', label: 'Recycled' },
	{ value: 'unsubscribed', label: 'Unsubscribed' },
	{ value: 'suppressed', label: 'Suppressed' },
	{ value: 'failed_permanent', label: 'Failed' }
];

const c_BadgeVariants = {
	pending: 'warning',
	queued: 'info',
	sent: 'primary',
	replied: 'success',
	bounced: 'danger',
	complained: 'danger',
	recycled: 'secondary',
	unsubscribed: 'dark',
	suppressed: 'danger',
	failed_permanent: 'danger'
};

const format_date_time = (value) => {
	if(!value) {
		return '-';
	}

	const date = new Date(value);
	if(isNaN(date.getTime())) {
		return '-';
	}

	return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) +
			' ' +
			date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const get_badge_variant = (status) => {
	if(status && c_BadgeVariants[status]) {
		return c_BadgeVariants[status];
	}

	return 'secondary';
};

function OutreachRecipientTable({ test_status_version }) {
	const navigate = useNavigate();

	const [outreach_recipients, set_outreach_recipients] = useState([]);
	const [total, set_total] = useState(0);
	const [loading, set_loading] = useState(true);
	const [error, set_error] = useState('');
	const [search_text, set_search_text] = useState('');
	const [status_filter, set_status_filter] = useState('');
	const [page, set_page] = useState(0);
	const [rows_per_page, set_rows_per_page] = useState(c_DefaultRowsPerPage);
	const [test_mode, set_test_mode] = useState(false);
	const [resetting, set_resetting] = useState(false);

	// True while the reset is waiting to be confirmed
	const [confirm_reset, set_confirm_reset] = useState(false);

	// Ticked recipients, held by id so a selection survives paging and filtering
	const [selected_recipient_ids, set_selected_recipient_ids] = useState([]);
	const [confirm_delete, set_confirm_delete] = useState(false);
	const [deleting, set_deleting] = useState(false);

	// Paging and filtering happen on the server, so every change refetches rather than slicing locally
	const load_outreach_recipients = async () => {
		set_loading(true);
		set_error('');

		try {
			const result = await coldemailer_api.get_outreach_recipients({
				page: page,
				per_page: rows_per_page,
				status: status_filter,
				email: search_text
			});

			set_outreach_recipients(result.outreach_recipients);
			set_total(result.total);
		} catch (error) {
			console.log('OutreachRecipientTable.load_outreach_recipients - ERROR: ', error);
			set_error('Failed to load outreach recipients.');
		} finally {
			set_loading(false);
		}
	};

	const load_test_status = async () => {
		try {
			const status = await coldemailer_api.get_test_status();
			set_test_mode(status && status.test_mode ? true : false);
		} catch (error) {
			console.log('OutreachRecipientTable.load_test_status - ERROR: ', error);
		}
	};

	useEffect(function() {
		load_test_status();
	}, [test_status_version]);

	// Puts every recipient currently listed back to a clean state, so the same sequence can be
	// run again. Only offered in test mode, and the service refuses it otherwise.
	const on_reset_click = () => {
		set_confirm_reset(true);
	};

	const on_cancel_reset = () => {
		if(resetting) {
			return;
		}

		set_confirm_reset(false);
	};

	const handle_reset = async () => {
		set_confirm_reset(false);
		set_resetting(true);
		set_error('');

		try {
			const emails = outreach_recipients.map(function(recipient) {
				return recipient.email;
			});

			await coldemailer_api.test_reset_recipients(emails);
			await load_outreach_recipients();
		} catch (error) {
			console.log('OutreachRecipientTable.handle_reset - ERROR: ', error);
			set_error('Failed to reset the recipients.');
		} finally {
			set_resetting(false);
		}
	};

	// Typing refetches, so the request is delayed until the user pauses
	useEffect(function() {
		const timer = setTimeout(function() {
			load_outreach_recipients();
		}, 300);

		return function() {
			clearTimeout(timer);
		};
	}, [page, rows_per_page, status_filter, search_text]);

	const total_pages = Math.max(1, Math.ceil(total / rows_per_page));
	const first_row = page * rows_per_page;

	const on_search_change = (event) => {
		set_search_text(event.target.value);
		set_page(0);
	};

	const on_status_filter_change = (event) => {
		set_status_filter(event.target.value);
		set_page(0);
	};

	const on_rows_per_page_change = (event) => {
		set_rows_per_page(parseInt(event.target.value));
		set_page(0);
	};

	const on_previous_page = () => {
		if(page > 0) {
			set_page(page - 1);
		}
	};

	const on_next_page = () => {
		if(page < total_pages - 1) {
			set_page(page + 1);
		}
	};

	const on_row_click = (recipient) => {
		navigate(coldemailer_config.outreach_recipient_route + '/' + recipient.id);
	};

	const is_recipient_selected = (recipient_id) => {
		return selected_recipient_ids.indexOf(recipient_id) !== -1;
	};

	const on_recipient_toggle = (recipient_id) => {
		if(is_recipient_selected(recipient_id)) {
			set_selected_recipient_ids(selected_recipient_ids.filter(function(id) {
				return id !== recipient_id;
			}));

			return;
		}

		set_selected_recipient_ids(selected_recipient_ids.concat([recipient_id]));
	};

	// This table is paged by the server, so only the rows in view can be ticked in one go. Rows
	// already ticked on another page stay ticked: the selection is held by id, not by what is
	// currently on screen.
	const is_page_fully_selected = () => {
		if(outreach_recipients.length === 0) {
			return false;
		}

		for(var check_index = 0; check_index < outreach_recipients.length; ++check_index) {
			if(!is_recipient_selected(outreach_recipients[check_index].id)) {
				return false;
			}
		}

		return true;
	};

	const on_select_all_toggle = () => {
		const page_ids = outreach_recipients.map(function(recipient) {
			return recipient.id;
		});

		if(is_page_fully_selected()) {
			set_selected_recipient_ids(selected_recipient_ids.filter(function(id) {
				return page_ids.indexOf(id) === -1;
			}));

			return;
		}

		const added = selected_recipient_ids.slice();

		for(var add_index = 0; add_index < page_ids.length; ++add_index) {
			if(added.indexOf(page_ids[add_index]) === -1) {
				added.push(page_ids[add_index]);
			}
		}

		set_selected_recipient_ids(added);
	};

	const on_delete_click = () => {
		set_confirm_delete(true);
	};

	const on_cancel_delete = () => {
		if(deleting) {
			return;
		}

		set_confirm_delete(false);
	};

	// Removes the rows outright. The addresses are not suppressed, so anything deleted here can be
	// queued again later.
	const handle_delete = async () => {
		set_confirm_delete(false);
		set_deleting(true);
		set_error('');

		try {
			await coldemailer_api.delete_outreach_recipients(selected_recipient_ids);

			set_selected_recipient_ids([]);
			await load_outreach_recipients();
		} catch (error) {
			console.log('OutreachRecipientTable.handle_delete - ERROR: ', error);
			set_error('Failed to delete the selected recipients.');
		} finally {
			set_deleting(false);
		}
	};

	const render_panel_header = () => {
		return (
			<div className="ce-panel-header">
				<h5 className="ce-panel-title">Outreach Recipients</h5>

				<div className="ce-header-actions">
					{/* The count is shown next to the button so it is clear how many rows are
					    actually ticked rather than leaving it to be worked out by paging around */}
					{selected_recipient_ids.length > 0 ? (
						<span className="ce-selection-count">
							{selected_recipient_ids.length + ' selected'}
						</span>
					) : null}

					{/* Always shown, and disabled until something is ticked, so the action is visible
					    rather than having to be discovered by selecting a row first */}
					<Button
						variant="danger"
						size="sm"
						className="ce-delete-button"
						onClick={on_delete_click}
						disabled={deleting || selected_recipient_ids.length === 0}
					>
						{deleting ? 'Deleting...' : 'Delete Selected'}
					</Button>

					{test_mode ? (
						<Button
							variant="danger"
							size="sm"
							className="ce-delete-button"
							onClick={on_reset_click}
							disabled={resetting || outreach_recipients.length === 0}
						>
							{resetting ? 'Resetting...' : 'Reset Listed'}
						</Button>
					) : null}

					<Button variant="outline-light" size="sm" className="ce-button" onClick={load_outreach_recipients}>
						Refresh
					</Button>
				</div>
			</div>
		);
	};

	const render_toolbar = () => {
		return (
			<div className="ce-toolbar">
				<Form.Group className="ce-field ce-field-search">
					<Form.Label className="ce-label">Search</Form.Label>
					<Form.Control
						type="text"
						placeholder="Filter by email address"
						value={search_text}
						onChange={on_search_change}
					/>
				</Form.Group>

				<Form.Group className="ce-field ce-field-status-filter">
					<Form.Label className="ce-label">Status</Form.Label>
					<Form.Select value={status_filter} onChange={on_status_filter_change}>
						{c_StatusFilterOptions.map(function(option) {
							return (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							);
						})}
					</Form.Select>
				</Form.Group>

				<Form.Group className="ce-field ce-field-rows">
					<Form.Label className="ce-label">Rows</Form.Label>
					<Form.Select value={rows_per_page} onChange={on_rows_per_page_change}>
						{c_RowsPerPageOptions.map(function(option) {
							return (
								<option key={option} value={option}>
									{option}
								</option>
							);
						})}
					</Form.Select>
				</Form.Group>

				<div className="ce-spacer"></div>
			</div>
		);
	};

	const render_empty_row = () => {
		return (
			<tr>
				<td colSpan={6} className="ce-empty">
					{search_text || status_filter ? 'No recipients match your filters.' : 'No outreach recipients yet.'}
				</td>
			</tr>
		);
	};

	const render_row = (recipient) => {
		return (
			<tr key={recipient.id} className="ce-row-clickable" onClick={function() { on_row_click(recipient); }}>
				<td
					className="ce-or-col-select"
					onClick={function(event) { event.stopPropagation(); }}
				>
					<Form.Check
						type="checkbox"
						checked={is_recipient_selected(recipient.id)}
						onChange={function() { on_recipient_toggle(recipient.id); }}
					/>
				</td>
				<td className="ce-or-col-email">
					{recipient.email ? recipient.email : '-'}
				</td>
				<td className="ce-or-col-status">
					<Badge bg={get_badge_variant(recipient.status)}>
						{recipient.status ? recipient.status : 'unknown'}
					</Badge>
				</td>
				<td className="ce-or-col-touches">
					{recipient.touch_count === null || recipient.touch_count === undefined ? '-' : recipient.touch_count}
				</td>
				<td className="ce-or-col-sent">
					{format_date_time(recipient.last_sent_at)}
				</td>
				<td className="ce-or-col-replied">
					{format_date_time(recipient.replied_at)}
				</td>
			</tr>
		);
	};

	const render_table = () => {
		return (
			<Table striped bordered hover responsive variant="dark" className="ce-table">
				<thead>
					<tr>
						<th className="ce-or-col-select" style={{paddingLeft: "12px"}}>
							<Form.Check
								type="checkbox"
								checked={is_page_fully_selected()}
								onChange={on_select_all_toggle}
							/>
						</th>
						<th className="ce-or-col-email">Email</th>
						<th className="ce-or-col-status">Status</th>
						<th className="ce-or-col-touches">Touches</th>
						<th className="ce-or-col-sent">Last Sent</th>
						<th className="ce-or-col-replied">Replied</th>
					</tr>
				</thead>
				<tbody>
					{outreach_recipients.length === 0 ? render_empty_row() : outreach_recipients.map(render_row)}
				</tbody>
			</Table>
		);
	};

	const render_pagination = () => {
		const last_row = Math.min(first_row + rows_per_page, total);
		const showing_from = total === 0 ? 0 : first_row + 1;

		return (
			<div className="ce-pagination">
				<div className="ce-pagination-info">
					{'Showing ' + showing_from + ' to ' + last_row + ' of ' + total}
				</div>

				<div className="ce-pagination-controls">
					<Button
						variant="outline-light"
						size="sm" className="ce-button"
						disabled={page === 0}
						onClick={on_previous_page}
					>
						Previous
					</Button>

					<span className="ce-page-label">
						{'Page ' + (page + 1) + ' of ' + total_pages}
					</span>

					<Button
						variant="outline-light"
						size="sm" className="ce-button"
						disabled={page >= total_pages - 1}
						onClick={on_next_page}
					>
						Next
					</Button>
				</div>
			</div>
		);
	};

	const render_body = () => {
		if(loading) {
			return (
				<div className="ce-loading">
					<Spinner animation="border" size="sm" />
				</div>
			);
		}

		return (
			<React.Fragment>
				{render_table()}
				{render_pagination()}
			</React.Fragment>
		);
	};

	// The reset only touches the addresses currently listed, not the whole table, so the count
	// shown is the number of rows on this page
	const render_confirm_reset_modal = () => {
		return (
			<Modal show={confirm_reset} onHide={on_cancel_reset} centered>
				<Modal.Header closeButton>
					<Modal.Title>Reset listed recipients</Modal.Title>
				</Modal.Header>

				<Modal.Body className="ce-modal-body">
					<p className="ce-modal-counts">
						{'Reset the ' + outreach_recipients.length + ' recipients currently listed to a clean state?'}
					</p>
					<p className="ce-modal-counts">
						Their status, touch count and send history are cleared so the sequence can run again.
					</p>
				</Modal.Body>

				<Modal.Footer>
					<Button variant="outline-secondary" onClick={on_cancel_reset} disabled={resetting}>
						Cancel
					</Button>

					<Button variant="danger" onClick={handle_reset} disabled={resetting}>
						{resetting ? 'Resetting...' : 'Reset'}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	// Deleting is not suppressing, and the difference decides whether the address can come back, so
	// the modal says which one this is rather than leaving it to be assumed.
	const render_confirm_delete_modal = () => {
		return (
			<Modal show={confirm_delete} onHide={on_cancel_delete} centered>
				<Modal.Header closeButton>
					<Modal.Title>Delete selected recipients</Modal.Title>
				</Modal.Header>

				<Modal.Body className="ce-modal-body">
					<p className="ce-modal-counts">
						{'Permanently delete the ' + selected_recipient_ids.length + ' selected recipients?'}
					</p>
					<p className="ce-modal-counts">
						Their prepared content, scheduled emails and list memberships go with them. The
						addresses are not suppressed, so they can be added again later.
					</p>
				</Modal.Body>

				<Modal.Footer>
					<Button variant="outline-secondary" onClick={on_cancel_delete} disabled={deleting}>
						Cancel
					</Button>

					<Button variant="danger" onClick={handle_delete} disabled={deleting}>
						{deleting ? 'Deleting...' : 'Delete'}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	return (
		<div className="ce-panel ce-recipients-panel">
			{render_panel_header()}

			{error ? <Alert variant="danger">{error}</Alert> : null}

			{render_toolbar()}
			{render_body()}

			{render_confirm_reset_modal()}
			{render_confirm_delete_modal()}
		</div>
	);
}

export default OutreachRecipientTable;
